import { CreatorRewardRecordEligibility, PayoutTimeGranularity } from "../../gql-generated/types";
import { useLensPayoutHistory } from "../../lens-creator-payout/hooks/useLensPayoutHistory";
import { usePayoutStatsHistory } from "../../lens-insights/hooks/usePayoutStatsHistory";
import { type ApolloResult } from "../../shared/models";
import { type TopPerformerAward } from "../models";
import { buildTopPerformerAwards } from "../utils";

import { useCreatorRewardRecord } from "./useCreatorRewardRecord";

const HISTORY_LIMIT = 50;

interface UseTopPerformerAwardsParams {
    lensId: string;
    unlockableId: string;
}

type TopPerformerAwardsResult = ApolloResult<{
    totalEarnedMilliUsd: number;
    awards: TopPerformerAward[];
}>;

export const useTopPerformerAwards = ({
    lensId,
    unlockableId,
}: UseTopPerformerAwardsParams): TopPerformerAwardsResult => {
    const lifetime = useLensPayoutHistory({ lensId });
    const rewardRecord = useCreatorRewardRecord({ lensId });
    // Not program-scoped; the section only shows this panel for lenses never in Lens+.
    const history = usePayoutStatsHistory({
        countryFilters: [],
        granularity: PayoutTimeGranularity.TimeGranularityMonthly,
        limit: HISTORY_LIMIT,
        unlockableId,
    });

    // postUU is only final once the reward is issued; matching it to the single award is best effort.
    const qualifiedPosters =
        rewardRecord.rewardEligibility === CreatorRewardRecordEligibility.RewardEligibilityIssued
            ? rewardRecord.postUU
            : 0;

    return {
        loading: lifetime.loading || rewardRecord.loading || history.loading,
        error: lifetime.error ?? history.error,
        totalEarnedMilliUsd: lifetime.totalEarningsMilliUsd,
        awards: buildTopPerformerAwards(history.periods, qualifiedPosters, lifetime.lastPayoutDate),
    };
};
